<template>
  <div class="flow-toggle" :class="{'flow-toggle--stacked': showLabel}">
    <button
      type="button"
      class="flow-toggle__seal"
      role="switch"
      :aria-checked="String(modelSigned)"
      :aria-label="actionLabel"
      :title="actionLabel"
      :disabled="busy || !characterId"
      :style="{'--seal-size': size + 'px'}"
      @click="toggle"
    >
      <img class="flow-toggle__art" :src="artUrl" alt="" draggable="false"/>
      <span class="flow-toggle__ring" aria-hidden="true"></span>
      <span v-if="busy" class="flow-toggle__spinner" aria-hidden="true"></span>
    </button>

    <span v-if="showLabel" class="flow-toggle__label">{{ stateLabel }}</span>

    <p v-if="error" class="flow-toggle__error" role="alert">{{ error }}</p>
  </div>
</template>

<script>
import {mediaUrl} from '@/components/Player/mediaUrl';
import {gameMasterCharacterService} from '@/services/GameMasterCharacterService.js';

export default {
  name: 'FlowSignToggle',
  props: {
    characterId: {type: String, default: null},
    signed: {type: Boolean, default: false},
    size: {type: Number, default: 44},
    showLabel: {type: Boolean, default: false}
  },
  emits: ['changed'],
  data() {
    return {
      busy: false,
      error: null,
      optimistic: null
    };
  },
  computed: {
    modelSigned() {
      return this.optimistic === null ? this.signed : this.optimistic;
    },
    artUrl() {
      return mediaUrl('/media/icons/specialActions/flow-sign.webp');
    },
    stateLabel() {
      return this.modelSigned ? 'Signed with Flow' : 'Sign with Flow';
    },
    actionLabel() {
      return this.modelSigned
        ? 'Release this character from Flow'
        : 'Sign this character with Flow';
    }
  },
  watch: {
    signed() {
      this.optimistic = null;
    }
  },
  methods: {
    async toggle() {
      if (this.busy || !this.characterId) return;
      const next = !this.modelSigned;
      this.busy = true;
      this.error = null;
      try {
        await gameMasterCharacterService.setGameMasterMode(this.characterId, next);
        this.optimistic = next;
        this.$emit('changed', next);
      } catch (e) {
        this.error = next
          ? 'Could not sign the character. Try again.'
          : 'Could not release the character. Try again.';
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped>
.flow-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.flow-toggle--stacked {
  flex-wrap: wrap;
}

.flow-toggle__seal {
  position: relative;
  width: var(--seal-size);
  height: var(--seal-size);
  flex: 0 0 auto;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 45%, #14171a 0%, #06080a 100%);
  cursor: pointer;
  isolation: isolate;
  transition: transform 0.18s ease, filter 0.18s ease;
}

.flow-toggle__seal:disabled {
  cursor: progress;
}

.flow-toggle__seal:hover:not(:disabled) {
  transform: translateY(-1px);
}

.flow-toggle__seal:focus-visible {
  outline: 2px solid var(--game-accent, #ffd700);
  outline-offset: 3px;
}

.flow-toggle__art {
  position: absolute;
  inset: 8%;
  width: 84%;
  height: 84%;
  border-radius: 50%;
  object-fit: cover;
  mix-blend-mode: screen;
  filter: grayscale(0.85) brightness(0.7);
  opacity: 0.55;
  transition: filter 0.25s ease, opacity 0.25s ease;
}

.flow-toggle__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid rgba(255, 215, 0, 0.35);
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}

/* Signed: the seal lights. The medallion is the state readout, so the art
   itself carries it rather than a separate indicator. */
[aria-checked="true"] .flow-toggle__art {
  filter: brightness(1.35) saturate(1.3) contrast(1.1);
  opacity: 1;
}

[aria-checked="true"] .flow-toggle__ring {
  border-color: #ff00e5;
  box-shadow: 0 0 10px rgba(255, 0, 229, 0.55), inset 0 0 8px rgba(255, 0, 229, 0.35);
  animation: flow-seal-pulse 2.6s ease-in-out infinite;
}

.flow-toggle__spinner {
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  border: 2px solid transparent;
  border-top-color: var(--game-accent, #ffd700);
  animation: flow-seal-spin 0.7s linear infinite;
}

.flow-toggle__label {
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--game-accent, #ffd700);
  white-space: nowrap;
}

.flow-toggle__error {
  flex-basis: 100%;
  margin: 0;
  font-size: 0.7rem;
  color: #ff8a8a;
}

@keyframes flow-seal-pulse {
  0%, 100% { box-shadow: 0 0 8px rgba(255, 0, 229, 0.45), inset 0 0 6px rgba(255, 0, 229, 0.3); }
  50% { box-shadow: 0 0 16px rgba(255, 0, 229, 0.75), inset 0 0 10px rgba(255, 0, 229, 0.45); }
}

@keyframes flow-seal-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .flow-toggle__seal,
  .flow-toggle__art,
  .flow-toggle__ring { transition: none; }
  [aria-checked="true"] .flow-toggle__ring { animation: none; }
  .flow-toggle__spinner { animation-duration: 2s; }
}
</style>
